"use client";

// Les deux scènes animées du catalogue des formations : la nuit d'Hermes (bloc
// de lancement) et un challenge interactif (bloc des formations refaites).
//
// Chacune rejoue une petite histoire en boucle, parce qu'elle montre ce qu'une
// phrase ne prouve pas : l'agent qui travaille seul, le code qui s'écrit et
// BeBot qui le lit. La charte demande que rien ne tourne en boucle dans
// l'interface ; ces deux scènes sont des démonstrations, pas des éléments
// d'interface, et LN les a voulues animées (« tu as retiré tout le côté
// automatique »). Les précautions, reprises du bandeau Hermes d'origine :
//   - une scène ne tourne que si elle est à l'écran (IntersectionObserver) ;
//   - qui a demandé moins d'animations voit l'état final, immobile ;
//   - chaque zone a sa hauteur réservée, rien ne saute d'une étape à l'autre ;
//   - une longue pause sur l'état final laisse le temps de lire avant de repartir.
//
// Tout se déduit d'une seule horloge (le temps écoulé dans la boucle) : pas
// d'enchaînement de minuteries à garder en phase.

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

const EASE = [0.22, 1, 0.36, 1];

/**
 * L'horloge d'une scène : le temps écoulé dans la boucle, en millisecondes.
 * Elle avance seulement quand la scène est visible, et reste sur `still` (l'état
 * final) pour qui a demandé moins d'animations.
 */
function useSceneClock(total, still) {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const [onScreen, setOnScreen] = useState(false);
    const [time, setTime] = useState(still);

    useEffect(() => {
        const node = ref.current;
        if (!node || typeof IntersectionObserver === "undefined") return undefined;
        const observer = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting), { threshold: 0.35 });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (reduce || !onScreen) return undefined;
        let frame;
        let last = performance.now();
        let elapsed = 0;
        let shown = -1;
        const tick = (now) => {
            elapsed = (elapsed + (now - last)) % total;
            last = now;
            // Un rendu toutes les 40 ms suffit : la frappe est à ce rythme.
            const step = Math.floor(elapsed / 40);
            if (step !== shown) {
                shown = step;
                setTime(elapsed);
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [reduce, onScreen, total]);

    return [ref, reduce ? still : time];
}

/** Un élément qui arrive : fondu et 12 px, la courbe de la charte. */
function Arrive({ show, children, className = "" }) {
    return (
        <AnimatePresence initial={false}>
            {show && (
                <motion.div
                    className={className}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, transition: { duration: 0.25 } }}
                    transition={{ duration: 0.4, ease: EASE }}
                >
                    {children}
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/** La coche qui se trace, sur fond orange. */
function Check({ className = "h-5 w-5" }) {
    return (
        <span className={`flex ${className} shrink-0 items-center justify-center rounded-full bg-[#E8511D]`}>
            <svg className="h-[58%] w-[58%]" viewBox="0 0 20 20" fill="none">
                <motion.path
                    d="M5 10.5l3.2 3.2L15 7"
                    stroke="#fff"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.3, delay: 0.1, ease: EASE }}
                />
            </svg>
        </span>
    );
}

// ---------------------------------------------------------------------------
// La nuit d'Hermes
// ---------------------------------------------------------------------------

// Trois plans dans le même cadre, sous une horloge qui ne s'arrête pas :
//   1. LE SOIR, 23 h 41 : vous confiez une tâche depuis votre messagerie.
//   2. LA NUIT : l'horloge file jusqu'à 3 h 12 pendant qu'Hermes avance, étape
//      après étape, chacune d'abord en cours puis cochée.
//   3. LE RÉSULTAT : son message, ce qu'il a retenu, et « Vous dormiez. »
// Une première version ne montrait que le dernier plan : on voyait le
// résultat, pas l'agent en train de travailler seul (LN : « tu peux faire
// beaucoup mieux »).
const STEPS = ["12 factures relues", "Martin écarté : il a payé hier soir", "3 relances rédigées", "Envoi programmé mardi à 9 h"];
const STEP_EVERY = 1150;
const NIGHT = (() => {
    const reply = 1500;
    const work = 3300;
    const result = work + STEPS.length * STEP_EVERY + 700;
    const punch = result + 1500;
    const end = punch + 4800;
    return { reply, work, result, punch, end, total: end + 700 };
})();
const EVENING = 23 * 60 + 41;
const LATE = 27 * 60 + 12; // 3 h 12, le lendemain

/** L'heure affichée : elle file pendant la nuit, en ralentissant à l'arrivée. */
function clockAt(time) {
    const progress = Math.max(0, Math.min(1, (time - NIGHT.work) / (NIGHT.result - NIGHT.work - 400)));
    const eased = 1 - (1 - progress) ** 3;
    const minutes = Math.round(EVENING + (LATE - EVENING) * eased) % (24 * 60);
    return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

/** Quelques étoiles, qui scintillent à peine : c'est la nuit. */
const STARS = [
    [8, 14, 0],
    [22, 62, 1.2],
    [37, 28, 2.1],
    [58, 8, 0.6],
    [71, 48, 1.7],
    [86, 20, 2.6],
    [93, 70, 0.9],
    [48, 82, 1.4],
];

const shot = {
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.25 } },
};
const bubble = "rounded-2xl px-4 py-3 text-base leading-snug";

export function HermesNightScene() {
    const [ref, time] = useSceneClock(NIGHT.total, NIGHT.end - 1);
    const live = time < NIGHT.end;
    const plan = !live ? "none" : time < NIGHT.work ? "evening" : time < NIGHT.result ? "night" : "result";
    const working = plan === "night";
    const done = Math.floor((time - NIGHT.work) / STEP_EVERY);
    const progress = Math.max(0, Math.min(1, (time - NIGHT.work) / (STEPS.length * STEP_EVERY)));

    return (
        <div ref={ref} className="relative mx-auto w-full max-w-md" aria-hidden="true">
            <div className="pointer-events-none absolute -inset-x-6 -inset-y-8">
                {STARS.map(([left, top, delay]) => (
                    <motion.span
                        key={`${left}-${top}`}
                        className="absolute h-[3px] w-[3px] rounded-full bg-white"
                        style={{ left: `${left}%`, top: `${top}%` }}
                        animate={{ opacity: [0.12, 0.45, 0.12] }}
                        transition={{ duration: 3.6, repeat: Infinity, delay, ease: "easeInOut" }}
                    />
                ))}
            </div>

            <div className="relative">
                <div className="mb-5 flex items-end justify-between">
                    <span className="flex items-center gap-2 text-sm text-white/55">
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#34D399] opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#34D399]" />
                        </span>
                        {working ? "Hermes travaille" : "Hermes · actif"}
                    </span>
                    {/* L'horloge : grande, parce que c'est elle qui raconte la nuit. */}
                    <span
                        className={`font-geistMono text-3xl font-semibold tabular-nums leading-none transition-colors duration-500 md:text-4xl ${working ? "text-[#FF9A76]" : "text-white"}`}
                    >
                        {live ? clockAt(time) : "03:12"}
                    </span>
                </div>

                {/* Les trois plans se succèdent dans la même place, réservée. */}
                <div className="relative min-h-[232px]">
                    <AnimatePresence mode="wait" initial={false}>
                        {plan === "evening" && (
                            <motion.div key="evening" {...shot} className="flex flex-col gap-3">
                                <div className="ml-auto max-w-[85%]">
                                    <p className="mb-1 text-right text-xs text-white/45">Vous</p>
                                    <p className={`mb-0 rounded-tr-md bg-[#E8511D] text-white ${bubble}`}>
                                        Relance mes factures en retard. Je vais dormir.
                                    </p>
                                </div>
                                <Arrive show={time >= NIGHT.reply} className="max-w-[85%]">
                                    <p className="mb-1 text-xs text-white/45">Hermes</p>
                                    <p className={`mb-0 rounded-tl-md bg-white/10 text-white ${bubble}`}>Je m&apos;en occupe. Bonne nuit.</p>
                                </Arrive>
                            </motion.div>
                        )}

                        {plan === "night" && (
                            <motion.div key="night" {...shot}>
                                <ul className="m-0 flex list-none flex-col gap-3 p-0">
                                    {STEPS.map((label, index) => {
                                        const state = done > index ? "done" : done === index ? "doing" : "todo";
                                        return (
                                            <li
                                                key={label}
                                                className={`flex items-center gap-3 text-base transition-colors duration-300 ${
                                                    state === "todo" ? "text-white/25" : state === "doing" ? "text-white" : "text-white/70"
                                                }`}
                                            >
                                                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                                                    {state === "done" && <Check />}
                                                    {state === "doing" && (
                                                        <motion.span
                                                            className="h-4 w-4 rounded-full border-2 border-white/25 border-t-[#FF9A76]"
                                                            animate={{ rotate: 360 }}
                                                            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                                                        />
                                                    )}
                                                    {state === "todo" && <span className="h-1.5 w-1.5 rounded-full bg-white/25" />}
                                                </span>
                                                {label}
                                            </li>
                                        );
                                    })}
                                </ul>
                                <div className="mt-6 h-1 overflow-hidden rounded-full bg-white/10">
                                    <div className="h-full rounded-full bg-[#E8511D]" style={{ width: `${progress * 100}%` }} />
                                </div>
                            </motion.div>
                        )}

                        {plan === "result" && (
                            <motion.div key="result" {...shot}>
                                <p className="mb-1 text-xs text-white/45">Hermes</p>
                                <p className={`mb-0 rounded-tl-md bg-white/10 text-white ${bubble}`}>
                                    Vos trois relances sont prêtes. J&apos;ai retiré la facture de Martin, il a payé hier soir.
                                </p>
                                <p className="mb-0 mt-3 flex items-center gap-2 text-sm text-white/70">
                                    <Check />
                                    Il a retenu comment faire, pour la prochaine fois.
                                </p>
                                <div className="mt-6 min-h-[40px]">
                                    <Arrive show={time >= NIGHT.punch}>
                                        <p className="mb-0 font-sourceSans text-3xl font-bold leading-none text-white md:text-4xl">Vous dormiez.</p>
                                    </Arrive>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Un challenge interactif
// ---------------------------------------------------------------------------

// Le code tapé, sans la balise fermante oubliée ; puis la correction.
const CODE = ["<main>", "  <h1>Mon portfolio", "  <p>Bienvenue sur mon site !</p>", "</main>"];
const FIX = "</h1>";
const LENGTH = CODE.reduce((sum, line) => sum + line.length, 0);
// Le nombre de caractères tapés avant chaque ligne.
const OFFSETS = CODE.map((_, index) => CODE.slice(0, index).reduce((sum, line) => sum + line.length, 0));
const KEY = 42; // millisecondes par caractère
const CHALLENGE = (() => {
    const typed = 500 + LENGTH * KEY;
    const hint = typed + 500;
    const fixStart = hint + 2600;
    const fixed = fixStart + FIX.length * 130;
    const praise = fixed + 400;
    const end = praise + 4200;
    return { start: 500, typed, hint, fixStart, fixed, praise, end, total: end + 600 };
})();

/** Une ligne de code, ses balises en couleur. `flag` surligne la balise ouverte et jamais fermée. */
function CodeLine({ text, flag }) {
    return text.split(/(<\/?[a-z0-9]*>?)/g).map((part, index) => {
        if (!part.startsWith("<")) return <span key={index}>{part}</span>;
        const flagged = flag && part === "<h1>";
        return (
            <span
                key={index}
                className={`rounded px-0.5 text-[#FF9A76] transition-colors duration-300 ${flagged ? "bg-[#E8511D]/30" : "bg-transparent"}`}
            >
                {part}
            </span>
        );
    });
}

/**
 * Le code se tape, l'aperçu suit lettre après lettre, BeBot repère la balise
 * oubliée, elle se referme, et le challenge est validé. BeBot tutoie, et il est
 * à l'écran.
 */
export function ChallengeScene() {
    const [ref, time] = useSceneClock(CHALLENGE.total, CHALLENGE.end - 1);
    const live = time < CHALLENGE.end;

    // Combien de caractères sont tapés, répartis ligne par ligne.
    const typedCount = live ? Math.max(0, Math.min(LENGTH, Math.floor((time - CHALLENGE.start) / KEY))) : 0;
    const lines = CODE.map((line, index) => line.slice(0, Math.max(0, typedCount - OFFSETS[index])));
    const fix = live && time >= CHALLENGE.fixStart ? FIX.slice(0, Math.min(FIX.length, Math.ceil((time - CHALLENGE.fixStart) / 130))) : "";
    const fixed = fix.length === FIX.length;
    const hinting = live && time >= CHALLENGE.hint && !fixed;
    const praising = live && time >= CHALLENGE.praise;

    // Le curseur : sur la ligne en cours de frappe, puis au bout de la ligne 2 pendant la correction.
    const typingLine = lines.findIndex((shown, index) => shown.length < CODE[index].length);
    const cursorLine = time >= CHALLENGE.fixStart && !fixed ? 1 : typingLine;

    // L'aperçu se lit dans ce qui est tapé.
    const title = lines[1].replace(/^\s*<h1>?/, "").replace(/^\s*<h?1?$/, "");
    const paragraph = lines[2]
        .replace(/^\s*<p>?/, "")
        .replace(/<\/?p?>?$/, "")
        .replace(/^\s*<?$/, "");

    return (
        <div ref={ref} aria-hidden="true">
            <div className="mx-auto w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-[0_24px_50px_-30px_rgba(25,39,52,0.45)] dark:bg-[#15202B]">
                <pre className="m-0 min-h-[124px] overflow-hidden bg-[#101826] px-4 py-3.5 font-geistMono text-[13px] leading-6 text-white/90">
                    <code>
                        {CODE.map((_, index) => (
                            <span key={index} className="block">
                                <span className="select-none text-white/35">{index + 1} </span>
                                <CodeLine text={lines[index] + (index === 1 ? fix : "")} flag={hinting} />
                                {live && cursorLine === index && (
                                    <motion.span
                                        className="ml-px inline-block h-4 w-[2px] translate-y-[3px] bg-[#FF9A76]"
                                        animate={{ opacity: [1, 1, 0, 0] }}
                                        transition={{ duration: 0.9, repeat: Infinity, times: [0, 0.5, 0.5, 1] }}
                                    />
                                )}
                            </span>
                        ))}
                    </code>
                </pre>
                <div className="relative min-h-[96px] border-t border-gray-200 px-4 py-3.5 dark:border-[#38444D]">
                    <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">Aperçu</p>
                    <p className="mb-0 min-h-7 font-sourceSans text-xl font-bold text-[#192734] dark:text-white">{title}</p>
                    <p className="mb-0 min-h-5 text-sm text-gray-600 dark:text-gray-400">{paragraph}</p>
                    <Arrive
                        show={praising}
                        className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-[#FFE3DA] py-1 pl-1 pr-2.5 text-xs font-semibold text-[#B83C12]"
                    >
                        <Check className="h-4 w-4" />
                        100 / 100
                    </Arrive>
                </div>
            </div>

            {/* BeBot : d'abord l'indice, puis le bravo. La place est réservée. */}
            <div className="mx-auto mt-3 flex min-h-[64px] w-full max-w-md items-start gap-2.5">
                <Image src="/images/bebot/bebot-profile.jpg" alt="" width={72} height={72} className="h-9 w-9 shrink-0 rounded-full object-cover" />
                <div className="relative min-w-0 flex-1">
                    <AnimatePresence mode="wait" initial={false}>
                        {hinting && (
                            <motion.p
                                key="hint"
                                className="mb-0 rounded-2xl rounded-tl-md bg-white px-3.5 py-2.5 text-sm leading-snug text-[#192734] dark:bg-[#15202B] dark:text-white"
                                initial={{ opacity: 0, x: -12 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                                transition={{ duration: 0.35, ease: EASE }}
                            >
                                Belle structure ! Ta balise{" "}
                                <code className="font-geistMono text-[13px] text-[#B83C12] dark:text-[#FF9A76]">&lt;h1&gt;</code> n&apos;est pas
                                fermée, ligne 2.
                            </motion.p>
                        )}
                        {praising && (
                            <motion.p
                                key="praise"
                                className="mb-0 rounded-2xl rounded-tl-md bg-white px-3.5 py-2.5 text-sm leading-snug text-[#192734] dark:bg-[#15202B] dark:text-white"
                                initial={{ opacity: 0, x: -12 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                                transition={{ duration: 0.35, ease: EASE }}
                            >
                                Bien joué, c&apos;est toi qui l&apos;as trouvé. C&apos;est comme ça qu&apos;on apprend vraiment.
                            </motion.p>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}
